import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { api, errorMessage } from './api';
import { setLocale, t } from './i18n';
import { qk } from './query-keys';
import { useToast } from './toast';

export function useWorkspaceId(): string {
  const params = useParams({ strict: false }) as { workspaceId?: string };
  return params.workspaceId ?? '';
}

/** Finds an account by slug (preferred) or id — used for `/$account/...` URLs. */
export function findAccount<T extends { id: string; slug: string }>(accounts: T[], param: string): T | undefined {
  const p = param.toLowerCase();
  return accounts.find((a) => a.slug.toLowerCase() === p) ?? accounts.find((a) => a.id === param);
}

/** The account's "Needs you" inbox; realtime invalidates it, polling every 30 s is the fallback. */
export function useInbox(accountId: string | undefined) {
  return useQuery({
    queryKey: qk.accountInbox(accountId ?? ''),
    queryFn: () => api.accounts.inbox(accountId!),
    enabled: !!accountId,
    refetchInterval: 30_000,
  });
}

export function useMe() {
  return useQuery({ queryKey: qk.me, queryFn: () => api.auth.me(), staleTime: 5 * 60_000 });
}

/** The language from the profile wins over this browser's: a choice made on another device comes along. */
export function useProfileLocale() {
  const userLocale = useMe().data?.user.locale;
  useEffect(() => {
    if (userLocale) setLocale(userLocale);
  }, [userLocale]);
}

export function useWorkspace(workspaceId: string) {
  return useQuery({ queryKey: qk.workspace(workspaceId), queryFn: () => api.workspaces.get(workspaceId), enabled: !!workspaceId });
}

export function useDesks(workspaceId: string) {
  return useQuery({ queryKey: qk.desks(workspaceId), queryFn: () => api.desks.list(workspaceId), enabled: !!workspaceId });
}

export function useAgents(workspaceId: string) {
  return useQuery({ queryKey: qk.agents(workspaceId), queryFn: () => api.agents.list(workspaceId), enabled: !!workspaceId });
}

export function useConnections(workspaceId: string) {
  return useQuery({
    queryKey: qk.connections(workspaceId),
    queryFn: () => api.connections.list(workspaceId),
    enabled: !!workspaceId,
  });
}

export function useRunSecrets(workspaceId: string) {
  return useQuery({ queryKey: qk.secrets(workspaceId), queryFn: () => api.secrets.list(workspaceId), enabled: !!workspaceId });
}

export function useSkills(workspaceId: string) {
  return useQuery({ queryKey: qk.skills(workspaceId), queryFn: () => api.skills.list(workspaceId), enabled: !!workspaceId });
}

export function useRuntimeProfiles(workspaceId: string) {
  return useQuery({
    queryKey: qk.runtimeProfiles(workspaceId),
    queryFn: () => api.runtimeProfiles.list(workspaceId),
    enabled: !!workspaceId,
    // A usage check takes a few seconds; follow it until it reports.
    refetchInterval: (q) => (q.state.data?.some((p) => p.usageCheck?.status === 'running') ? 3000 : false),
  });
}

interface ApiMutationOptions<TData, TVars> {
  /** Toast shown on success (omit for silent). */
  success?: string | ((data: TData, vars: TVars) => string);
  /** Title of the error toast. */
  errorTitle?: string;
  /** Query keys to invalidate after success. */
  invalidate?: QueryKey[] | ((data: TData, vars: TVars) => QueryKey[]);
  onSuccess?: (data: TData, vars: TVars) => void;
}

/** useMutation with friendly German error toasts and cache invalidation. */
export function useApiMutation<TData, TVars = void>(
  fn: (vars: TVars) => Promise<TData>,
  opts: ApiMutationOptions<TData, TVars> = {},
) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation<TData, unknown, TVars>({
    mutationFn: fn,
    onSuccess: (data, vars) => {
      const keys = typeof opts.invalidate === 'function' ? opts.invalidate(data, vars) : (opts.invalidate ?? []);
      for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey });
      if (opts.success) toast.success(typeof opts.success === 'function' ? opts.success(data, vars) : opts.success);
      opts.onSuccess?.(data, vars);
    },
    onError: (err) => toast.error(opts.errorTitle ?? t('That didn’t work'), errorMessage(err)),
  });
}

/** Re-renders the component periodically (for relative timestamps). */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Parses a JSON object from a textarea; returns an error message instead of throwing. */
export function parseJsonObject(text: string): { value: Record<string, unknown> } | { error: string } {
  const trimmed = text.trim();
  if (!trimmed) return { value: {} };
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { error: t('Please enter a JSON object, e.g. {example}.', { example: '{ "key": "value" }' }) };
    }
    return { value: parsed as Record<string, unknown> };
  } catch (e) {
    return { error: t('That’s not valid JSON: {error}', { error: e instanceof Error ? e.message : String(e) }) };
  }
}

/** Whether the screen is a phone's (below Tailwind's `md`), following rotation and resizing. */
export function useIsPhone() {
  const query = '(max-width: 767px)';
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setPhone(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return phone;
}
